import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { GetFunctionsByEventId, AddFunction, UpdateFunction } from '../../api/api';
import { makeCrud } from '../../utils/crud';
import { useCrudList } from '../../utils/useCrudList';
import { SIDES, FUNCTION_TYPES, sideChipClass, fmtDate, isMarriageEventType } from '../../utils/helpers';

// Functions = the sub-events that make up one Event (Haldi, Sangeet, Wedding Cum
// Reception, …). Per the role model, the Event Owner / Event Admin manages them;
// members below them only invite guests per function. Event-scoped.
const getColumns = (isTwoSided) => [
  { key: 'FunctionName', label: 'Function' },
  { key: 'FunctionType', label: 'Type' },
  { key: 'FunctionDate', label: 'Date', render: r => fmtDate(r.FunctionDate) },
  { key: 'FunctionTime', label: 'Time' },
  ...(isTwoSided ? [{ key: 'Side', label: 'Side', render: r => <span className={sideChipClass(r.Side)}>{r.Side}</span> }] : []),
  { key: 'Venue', label: 'Venue' },
  { key: 'Status', label: 'Status', render: r => <span className="chip pend">{r.Status}</span> },
];

const getFields = (isTwoSided) => [
  { key: 'FunctionName', label: 'Function Name', live: true, required: true, half: true },
  { key: 'FunctionType', label: 'Type', type: 'select', options: FUNCTION_TYPES, half: true },
  { key: 'FunctionDate', label: 'Date', type: 'date', half: true },
  { key: 'FunctionTime', label: 'Time (e.g. 7:00 PM)', half: true },
  ...(isTwoSided ? [{ key: 'Side', label: 'Side', type: 'select', options: SIDES, default: 'Common', half: true }] : []),
  { key: 'Venue', label: 'Venue', live: true, half: true },
  { key: 'City', label: 'City', live: true, half: true },
  { key: 'VenueAddress', label: 'Venue Address', type: 'textarea' },
  { key: 'GpsLocation', label: 'GPS Location (lat,lng or Maps link — shown to guests)', type: 'textarea' },
  { key: 'Description', label: 'Description', type: 'textarea' },
];

export function useFunctions(event) {
  const { user, organiser, isAdmin } = useAuth();
  const nav = useNavigate();
  // Row click = drill in to the guests invited to this function (Esc comes back).
  const openRow = (row) => nav('/app/members?fn=' + row.FunctionId);
  const isTwoSided = isMarriageEventType(event?.EventTypeName || event?.EventType);
  const columns = useMemo(() => getColumns(isTwoSided), [isTwoSided]);
  const fields = useMemo(() => getFields(isTwoSided), [isTwoSided]);
  const load = useCallback(() => GetFunctionsByEventId({ EventId: event.EventId }), [event]);
  const { onSave: rawSave, onDelete } = makeCrud({
    add: AddFunction, update: UpdateFunction, idKey: 'FunctionId',
    ctx: { EventId: event.EventId, OrganiserId: organiser.OrganiserId }, userId: user.UserId,
  });
  const onSave = useCallback((values, editing) => rawSave({
    ...values,
    Side: isTwoSided ? (values.Side || 'Common') : 'Common',
    Status: editing?.Status || values.Status || 'Planned',
  }, editing), [rawSave, isTwoSided]);

  // Owner / Event Admin (isAdmin = SuperAdmin, org admin, or per-event admin).
  const can = { add: isAdmin, edit: isAdmin, delete: isAdmin };

  const vm = useCrudList({
    load, onSave, onDelete, fields, can,
    confirmDelete: (r) => `Delete function "${r.FunctionName || ''}"?`,
  });
  return { ...vm, columns, isAdmin, isTwoSided, openRow };
}
